import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Pagination, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import SourceBadge from '../components/SourceBadge';

type DataSource = { code: string; title: string; is_active: boolean };
type Subject = { id: number; name: string };
type ProductRow = {
  id: number | string;
  source_code: string;
  source_title?: string;
  source_color?: string | null;
  name: string;
  url?: string | null;
  thumb_url?: string | null;
  subject_id?: number | null;
  subject_name?: string | null;
  purchase_percent?: number | null;
  country?: string | null;
  revenue?: number | null;
  sales?: number | null;
  required_price?: number | null;
  package_length?: number | null;
  package_width?: number | null;
  package_height?: number | null;
  status_id?: string | null;
  status_name?: string | null;
  edit_locked?: boolean;
};

type Draft = {
  name: string;
  url: string;
  subject_id: string;
  required_price: string;
  purchase_percent: string;
  package_length: string;
  package_width: string;
  package_height: string;
  country: string;
  revenue: string;
  sales: string;
};

const PAGE_SIZE = 20;

const emptyForm = {
  source_code: 'manual',
  name: '',
  url: '',
  subject_id: '',
  required_price: '',
  package_length: '',
  package_width: '',
  package_height: '',
  country: '',
  revenue: '',
  sales: '',
  purchase_percent: '',
};

const cellInputStyle: React.CSSProperties = { minWidth: 72, fontSize: 12 };

function toDraft(p: ProductRow): Draft {
  return {
    name: p.name || '',
    url: p.url || '',
    subject_id: p.subject_id != null ? String(p.subject_id) : '',
    required_price: p.required_price != null ? String(p.required_price) : '',
    purchase_percent: p.purchase_percent != null ? String(p.purchase_percent) : '',
    package_length: p.package_length != null ? String(p.package_length) : '',
    package_width: p.package_width != null ? String(p.package_width) : '',
    package_height: p.package_height != null ? String(p.package_height) : '',
    country: p.country || '',
    revenue: p.revenue != null ? String(p.revenue) : '',
    sales: p.sales != null ? String(p.sales) : '',
  };
}

function isLocked(p: ProductRow) {
  return Boolean(p.edit_locked) || p.status_name === 'Расчет ЮЭ';
}

type EditRowProps = {
  product: ProductRow;
  subjects: Subject[];
  onSaved: () => void;
};

const EditRow: React.FC<EditRowProps> = ({ product, subjects, onSaved }) => {
  const locked = isLocked(product);
  const [draft, setDraft] = useState(() => toDraft(product));
  const [image, setImage] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(toDraft(product));
    setImage(null);
  }, [product]);

  const set = (key: keyof Draft, value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    if (locked) return;
    if (!draft.name.trim() || !draft.url.trim() || !draft.subject_id) {
      toast.error('Нужны название, URL и предмет');
      return;
    }
    if (!draft.required_price || Number(draft.required_price) <= 0) {
      toast.error('Цена МП > 0');
      return;
    }
    if (
      !draft.package_length ||
      !draft.package_width ||
      !draft.package_height ||
      Number(draft.package_length) <= 0 ||
      Number(draft.package_width) <= 0 ||
      Number(draft.package_height) <= 0
    ) {
      toast.error('Габариты L/W/H > 0');
      return;
    }
    if (draft.purchase_percent === '' || !Number.isFinite(Number(draft.purchase_percent))) {
      toast.error('Укажите % выкупа');
      return;
    }
    if (!product.thumb_url && !image) {
      toast.error('Нужно фото (thumb)');
      return;
    }

    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: draft.name.trim(),
        url: draft.url.trim(),
        subject_id: Number(draft.subject_id),
        required_price: Number(draft.required_price),
        purchase_percent: Number(draft.purchase_percent),
        package_length: Number(draft.package_length),
        package_width: Number(draft.package_width),
        package_height: Number(draft.package_height),
      };
      if (draft.country.trim()) payload.country = draft.country.trim();
      if (draft.revenue !== '') payload.revenue = Number(draft.revenue);
      if (draft.sales !== '') payload.sales = Number(draft.sales);

      const fd = new FormData();
      fd.append('productData', JSON.stringify(payload));
      if (image) fd.append('image', image);

      await api.patch(`/manual-products/${product.id}`, fd);
      toast.success('Сохранено');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (locked) {
    return (
      <tr>
        <td>
          {product.thumb_url ? (
            <img src={product.thumb_url} alt="" style={{ width: 40, height: 40, objectFit: 'cover' }} />
          ) : (
            '—'
          )}
        </td>
        <td>
          <SourceBadge
            code={product.source_code}
            title={product.source_title}
            color={product.source_color}
          />
        </td>
        <td>
          <div className="small fw-semibold">{product.name}</div>
          <div className="text-muted" style={{ fontSize: 11 }}>
            id {product.id}
          </div>
        </td>
        <td className="small">
          {product.url ? (
            <a href={product.url} target="_blank" rel="noreferrer">
              ссылка
            </a>
          ) : (
            '—'
          )}
        </td>
        <td className="small">{product.subject_name || '—'}</td>
        <td className="small">
          {product.required_price != null ? Number(product.required_price) : '—'}
        </td>
        <td className="small">{product.purchase_percent ?? '—'}</td>
        <td className="small">{product.package_length ?? '—'}</td>
        <td className="small">{product.package_width ?? '—'}</td>
        <td className="small">{product.package_height ?? '—'}</td>
        <td className="small">{product.country || '—'}</td>
        <td className="small">{product.revenue != null ? Number(product.revenue) : '—'}</td>
        <td className="small">{product.sales ?? '—'}</td>
        <td className="small">
          <span className="badge bg-success">{product.status_name || 'Расчет ЮЭ'}</span>
          <div className="text-muted mt-1" style={{ fontSize: 11 }}>
            только просмотр
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td style={{ minWidth: 90 }}>
        {product.thumb_url && (
          <img
            src={product.thumb_url}
            alt=""
            className="mb-1 d-block"
            style={{ width: 40, height: 40, objectFit: 'cover' }}
          />
        )}
        <Form.Control
          type="file"
          accept="image/*"
          size="sm"
          onChange={(e) => setImage((e.target as HTMLInputElement).files?.[0] || null)}
        />
      </td>
      <td>
        <SourceBadge
          code={product.source_code}
          title={product.source_title}
          color={product.source_color}
        />
        <div className="text-muted" style={{ fontSize: 11 }}>
          id {product.id}
        </div>
      </td>
      <td>
        <Form.Control
          size="sm"
          value={draft.name}
          onChange={(e) => set('name', e.target.value)}
          style={{ minWidth: 120, fontSize: 12 }}
        />
      </td>
      <td>
        <Form.Control
          size="sm"
          type="url"
          value={draft.url}
          onChange={(e) => set('url', e.target.value)}
          style={{ minWidth: 120, fontSize: 12 }}
        />
      </td>
      <td>
        <Form.Select
          size="sm"
          value={draft.subject_id}
          onChange={(e) => set('subject_id', e.target.value)}
          style={{ minWidth: 140, fontSize: 12 }}
        >
          <option value="">—</option>
          {subjects.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Form.Select>
      </td>
      <td>
        <Form.Control
          size="sm"
          type="number"
          step="any"
          value={draft.required_price}
          onChange={(e) => set('required_price', e.target.value)}
          style={cellInputStyle}
        />
      </td>
      <td>
        <Form.Control
          size="sm"
          type="number"
          step="any"
          value={draft.purchase_percent}
          onChange={(e) => set('purchase_percent', e.target.value)}
          style={cellInputStyle}
        />
      </td>
      <td>
        <Form.Control
          size="sm"
          type="number"
          step="any"
          value={draft.package_length}
          onChange={(e) => set('package_length', e.target.value)}
          style={cellInputStyle}
        />
      </td>
      <td>
        <Form.Control
          size="sm"
          type="number"
          step="any"
          value={draft.package_width}
          onChange={(e) => set('package_width', e.target.value)}
          style={cellInputStyle}
        />
      </td>
      <td>
        <Form.Control
          size="sm"
          type="number"
          step="any"
          value={draft.package_height}
          onChange={(e) => set('package_height', e.target.value)}
          style={cellInputStyle}
        />
      </td>
      <td>
        <Form.Control
          size="sm"
          value={draft.country}
          onChange={(e) => set('country', e.target.value)}
          style={cellInputStyle}
        />
      </td>
      <td>
        <Form.Control
          size="sm"
          type="number"
          step="any"
          value={draft.revenue}
          onChange={(e) => set('revenue', e.target.value)}
          style={cellInputStyle}
        />
      </td>
      <td>
        <Form.Control
          size="sm"
          type="number"
          value={draft.sales}
          onChange={(e) => set('sales', e.target.value)}
          style={cellInputStyle}
        />
      </td>
      <td>
        <div className="small text-muted mb-1">{product.status_name || 'без статуса'}</div>
        <Button size="sm" variant="outline-primary" disabled={saving} onClick={() => void save()}>
          {saving ? <Spinner size="sm" /> : 'Сохранить'}
        </Button>
      </td>
    </tr>
  );
};

/** Добавление в каталог строкой + список последних с пагинацией. */
const ManualProduct: React.FC = () => {
  const [form, setForm] = useState({ ...emptyForm });
  const [image, setImage] = useState<File | null>(null);
  const [sources, setSources] = useState<DataSource[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [items, setItems] = useState<ProductRow[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterSource, setFilterSource] = useState<string>('');

  const set = (key: keyof typeof emptyForm, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const loadList = useCallback(async (p: number, source: string) => {
    setListLoading(true);
    try {
      const res = await api.get('/manual-products', {
        params: {
          page: p,
          limit: PAGE_SIZE,
          ...(source ? { source_code: source } : {}),
        },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(res.data.page || p);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Не удалось загрузить список');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.all([api.get('/data-sources'), api.get('/subjects')])
      .then(([src, sub]) => {
        setSources((src.data.items || []).filter((s: DataSource) => s.is_active !== false));
        setSubjects(sub.data.data || sub.data.subjects || sub.data.items || []);
      })
      .catch((e: any) => toast.error(e?.response?.data?.error || 'Не удалось загрузить справочники'));
  }, []);

  useEffect(() => {
    void loadList(page, filterSource);
  }, [page, filterSource, loadList]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Укажите название');
      return;
    }
    if (!form.subject_id) {
      toast.error('Укажите предмет');
      return;
    }
    if (!form.required_price || Number(form.required_price) <= 0) {
      toast.error('Укажите цену МП > 0');
      return;
    }
    if (
      !form.package_length ||
      !form.package_width ||
      !form.package_height ||
      Number(form.package_length) <= 0 ||
      Number(form.package_width) <= 0 ||
      Number(form.package_height) <= 0
    ) {
      toast.error('Укажите габариты L/W/H > 0');
      return;
    }
    if (!form.url.trim()) {
      toast.error('Укажите ссылку на карточку');
      return;
    }
    if (form.purchase_percent === '' || !Number.isFinite(Number(form.purchase_percent))) {
      toast.error('Укажите % выкупа');
      return;
    }
    if (!image) {
      toast.error('Загрузите фото (thumb)');
      return;
    }

    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        source_code: form.source_code || 'manual',
        name: form.name.trim(),
        subject_id: Number(form.subject_id),
        required_price: Number(form.required_price),
        package_length: Number(form.package_length),
        package_width: Number(form.package_width),
        package_height: Number(form.package_height),
        url: form.url.trim(),
        purchase_percent: Number(form.purchase_percent),
      };
      if (form.country.trim()) payload.country = form.country.trim();
      if (form.revenue !== '') payload.revenue = Number(form.revenue);
      if (form.sales !== '') payload.sales = Number(form.sales);

      const fd = new FormData();
      fd.append('productData', JSON.stringify(payload));
      fd.append('image', image);

      const res = await api.post('/manual-products', fd);
      toast.success(`Товар добавлен · id ${res.data.product?.id}`);
      setForm({ ...emptyForm, source_code: form.source_code });
      setImage(null);
      setPage(1);
      void loadList(1, filterSource);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-3">
      <h2 className="mb-2">Ручной товар</h2>
      <p className="text-muted mb-2">
        Добавление в каталог без проработки ТЗ. Источники:{' '}
        <Link to="/data-sources">Источники</Link>.
      </p>
      <Alert variant="secondary" className="py-2 mb-3">
        Строка сверху — новое добавление. Ниже — список: редактирование до статуса «Расчет ЮЭ».
      </Alert>

      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <Form.Label className="mb-0 small text-muted">Фильтр списка:</Form.Label>
        <Form.Select
          size="sm"
          style={{ width: 220 }}
          value={filterSource}
          onChange={(e) => {
            setFilterSource(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Все источники</option>
          {sources.map((s) => (
            <option key={s.code} value={s.code}>
              {s.title} ({s.code})
            </option>
          ))}
        </Form.Select>
        {listLoading && <Spinner size="sm" />}
        <span className="small text-muted">всего: {total}</span>
      </div>

      <Form onSubmit={submit}>
        <Table
          size="sm"
          bordered
          hover
          responsive
          variant="dark"
          className="table-dark align-middle"
        >
          <thead>
            <tr>
              <th>Фото</th>
              <th>Источник</th>
              <th>Название *</th>
              <th>URL *</th>
              <th>Предмет *</th>
              <th>Цена МП *</th>
              <th>Выкуп % *</th>
              <th>L *</th>
              <th>W *</th>
              <th>H *</th>
              <th>Страна</th>
              <th>Выручка</th>
              <th>Продажи</th>
              <th>Статус / действие</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ minWidth: 90 }}>
                <Form.Control
                  type="file"
                  accept="image/*"
                  size="sm"
                  onChange={(e) => setImage((e.target as HTMLInputElement).files?.[0] || null)}
                />
              </td>
              <td>
                <Form.Select
                  size="sm"
                  value={form.source_code}
                  onChange={(e) => set('source_code', e.target.value)}
                  style={cellInputStyle}
                >
                  {sources.length === 0 && <option value="manual">manual</option>}
                  {sources.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.code}
                    </option>
                  ))}
                </Form.Select>
              </td>
              <td>
                <Form.Control
                  size="sm"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  required
                  style={{ minWidth: 120, fontSize: 12 }}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  type="url"
                  placeholder="https://"
                  value={form.url}
                  onChange={(e) => set('url', e.target.value)}
                  required
                  style={{ minWidth: 120, fontSize: 12 }}
                />
              </td>
              <td>
                <Form.Select
                  size="sm"
                  value={form.subject_id}
                  onChange={(e) => set('subject_id', e.target.value)}
                  required
                  style={{ minWidth: 140, fontSize: 12 }}
                >
                  <option value="">—</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Form.Select>
              </td>
              <td>
                <Form.Control
                  size="sm"
                  type="number"
                  step="any"
                  value={form.required_price}
                  onChange={(e) => set('required_price', e.target.value)}
                  required
                  style={cellInputStyle}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  type="number"
                  step="any"
                  value={form.purchase_percent}
                  onChange={(e) => set('purchase_percent', e.target.value)}
                  required
                  style={cellInputStyle}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  type="number"
                  step="any"
                  value={form.package_length}
                  onChange={(e) => set('package_length', e.target.value)}
                  required
                  style={cellInputStyle}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  type="number"
                  step="any"
                  value={form.package_width}
                  onChange={(e) => set('package_width', e.target.value)}
                  required
                  style={cellInputStyle}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  type="number"
                  step="any"
                  value={form.package_height}
                  onChange={(e) => set('package_height', e.target.value)}
                  required
                  style={cellInputStyle}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  value={form.country}
                  onChange={(e) => set('country', e.target.value)}
                  style={cellInputStyle}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  type="number"
                  step="any"
                  value={form.revenue}
                  onChange={(e) => set('revenue', e.target.value)}
                  style={cellInputStyle}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  type="number"
                  value={form.sales}
                  onChange={(e) => set('sales', e.target.value)}
                  style={cellInputStyle}
                />
              </td>
              <td>
                <Button type="submit" size="sm" disabled={saving}>
                  {saving ? <Spinner size="sm" /> : 'Добавить'}
                </Button>
              </td>
            </tr>

            {items.map((p) => (
              <EditRow
                key={String(p.id)}
                product={p}
                subjects={subjects}
                onSaved={() => void loadList(page, filterSource)}
              />
            ))}

            {!listLoading && items.length === 0 && (
              <tr>
                <td colSpan={14} className="text-center text-muted py-3">
                  Пока нет добавленных товаров
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Form>

      {pages > 1 && (
        <Pagination size="sm" className="mt-2">
          <Pagination.Prev disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
          {Array.from({ length: Math.min(pages, 10) }, (_, i) => {
            const n = pages <= 10 ? i + 1 : Math.max(1, Math.min(page - 4, pages - 9)) + i;
            if (n > pages) return null;
            return (
              <Pagination.Item key={n} active={n === page} onClick={() => setPage(n)}>
                {n}
              </Pagination.Item>
            );
          })}
          <Pagination.Next
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
          />
        </Pagination>
      )}
    </div>
  );
};

export default ManualProduct;
